// src/utils/reporteVentasGenerator.js

// 👈 NUEVO: Importar función que obtiene los datos de la empresa actual
import { obtenerDatosEmpresa } from './tenantConfig';

// Configuración por defecto (fallback)
export const JEAN_INFORMATICO_CONFIG = {
  nombre: 'JEAN INFORMATICO C.A.',
  rif: 'J-41023119-0',
  slogan: 'Servicio Técnico Especializado',
  direccion: 'C.C. LATIN CENTER PB LOCAL 01',
  ciudad: 'ACARIGUA - PORTUGUESA',
  telefono: '0416-7594723',
  whatsapp: '04167594723',
  email: 'jean.informatico@gmail.com',
  sitioWeb: 'www.jeaninformatico.com.ve'
};

/**
 * 👈 NUEVO: Obtiene la config de la empresa actual con fallback
 */
const getEmpresa = () => {
  try {
    const datos = obtenerDatosEmpresa();
    return {
      nombre: datos.nombreComercial || datos.nombre || JEAN_INFORMATICO_CONFIG.nombre,
      rif: datos.rif || JEAN_INFORMATICO_CONFIG.rif,
      slogan: datos.slogan || JEAN_INFORMATICO_CONFIG.slogan,
      direccion: datos.direccion || JEAN_INFORMATICO_CONFIG.direccion,
      ciudad: datos.ciudad || JEAN_INFORMATICO_CONFIG.ciudad,
      telefono: datos.telefono1 || datos.telefono2 || JEAN_INFORMATICO_CONFIG.telefono,
      whatsapp: datos.whatsapp || datos.telefono1 || JEAN_INFORMATICO_CONFIG.whatsapp,
      email: datos.email || JEAN_INFORMATICO_CONFIG.email,
      sitioWeb: datos.sitioWeb || JEAN_INFORMATICO_CONFIG.sitioWeb,
      logoBase64: datos.logoBase64 || null,
    };
  } catch (err) {
    console.warn('⚠️ No se pudo obtener config del tenant, usando fallback:', err);
    return { ...JEAN_INFORMATICO_CONFIG, logoBase64: null };
  }
};

// ==================== UTILIDADES ====================

export const formatearFechaReporte = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return fechaISO;
  }
};

export const formatearFechaCorta = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return fechaISO;
  }
};

export const formatearNumero = (numero, decimales = 2) => {
  if (numero === null || numero === undefined) return '0.00';
  return new Intl.NumberFormat('es-VE', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales
  }).format(numero);
};

export const formatearMonedaUSD = (monto) => {
  if (monto === null || monto === undefined) return '$0.00';
  return `$${formatearNumero(monto, 2)}`;
};

export const formatearMonedaBs = (valor) => {
  if (valor === null || valor === undefined) return 'Bs 0,00';
  const numero = Number(valor);
  if (isNaN(numero)) return 'Bs 0,00';
  return new Intl.NumberFormat('es-VE', {
    style: 'currency',
    currency: 'VES',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numero).replace('VES', 'Bs');
};

export const convertirEstadoTexto = (estado) => {
  if (!estado) return 'DESCONOCIDO';
  const estadoLower = estado.toString().toLowerCase();
  if (estadoLower === 'activo' || estadoLower === 'true' || estadoLower === '1') return 'ACTIVO';
  if (estadoLower === 'inactivo' || estadoLower === 'false' || estadoLower === '0') return 'INACTIVO';
  return estado.toString().toUpperCase();
};

// ==================== LOGO ====================

let LOGO_BASE64 = '';

/**
 * 👈 MODIFICADO: Prioriza el logo del tenant; si no hay, usa /logo-jean.jpg
 */
export const cargarLogo = async () => {
  try {
    const empresa = getEmpresa();
    if (empresa.logoBase64) {
      const base64Limpio = empresa.logoBase64.includes(',')
        ? empresa.logoBase64.split(',')[1]
        : empresa.logoBase64;
      LOGO_BASE64 = base64Limpio;
      console.log('✅ Logo del tenant cargado (ventas)');
      return LOGO_BASE64;
    }

    const response = await fetch('/logo-jean.jpg');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        LOGO_BASE64 = reader.result.split(',')[1];
        resolve(LOGO_BASE64);
      };
      reader.onerror = () => {
        LOGO_BASE64 = '';
        resolve('');
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error cargando el logo:', error);
    LOGO_BASE64 = '';
    return '';
  }
};

export const getLogoBase64 = () => LOGO_BASE64;

// ==================== NORMALIZACIÓN ====================

export const normalizarVenta = (venta) => {
  if (!venta) return null;
  
  const tipoVenta = venta.tipoVenta || venta.tipo_venta || '';
  const esCredito = tipoVenta.toUpperCase().includes('CREDITO') || tipoVenta.toUpperCase().includes('CRÉDITO');
  
  let montoPagadoReal = 0;
  if (venta.pagos && Array.isArray(venta.pagos) && venta.pagos.length > 0) {
    montoPagadoReal = venta.pagos.reduce((sum, p) => sum + (parseFloat(p.montoUsd) || 0), 0);
  } else if (venta.montoPagado || venta.monto_pagado) {
    montoPagadoReal = Number(venta.montoPagado || venta.monto_pagado) || 0;
  }
  
  const totalUSD = venta.totalUSD || venta.totalUsd || venta.total_usd || 0;
  const montoCobradoParaCaja = esCredito ? montoPagadoReal : Number(totalUSD);
  
  return {
    idVenta: venta.idVenta,
    numeroFactura: venta.numeroFactura || venta.numero_factura || `FAC-${venta.idVenta || '000'}`,
    fechaVenta: venta.fechaVenta || venta.fecha_venta,
    tipoVenta: tipoVenta,
    totalUSD: totalUSD,
    totalBs: venta.totalBs || venta.total_bs || venta.total || 0,
    estado: venta.estado || venta.estadoOriginal || 'Completada',
    
    metodoPago: venta.metodoPago || venta.metodo_pago || '—',
    idTipoCredito: venta.idTipoCredito || venta.id_tipo_credito || venta.tipo_credito_id || venta.tipoCreditoId || null,
    esCredito: esCredito,
    
    idCliente: venta.idCliente,
    nombreCliente: venta.nombreCliente || venta.clienteNombre || venta.cliente_nombre || venta.cliente || 'Consumidor Final',
    
    idVendedor: venta.idVendedor || venta.id_vendedor,
    nombreVendedor: venta.nombreVendedor || venta.vendedorNombre || venta.vendedor_nombre || venta.vendedor || venta.usuarioNombre || '—',
    
    detalles: venta.detalles || venta.detallesVenta || [],
    pagos: venta.pagos || [],
    montoPagado: montoPagadoReal,
    montoCobradoParaCaja: montoCobradoParaCaja
  };
};

export const normalizarVentas = (ventas) => {
  if (!Array.isArray(ventas)) return [];
  return ventas.map(venta => normalizarVenta(venta));
};

// ==================== HELPERS ====================

export const formatearTipoVenta = (tipo, idTipoCredito = null) => {
  if (!tipo) return 'CONTADO';
  const tipoStr = tipo.toString().toUpperCase();
  
  if (tipoStr.includes('CRÉDITO') || tipoStr.includes('CREDITO') || tipoStr === '2') {
    return 'CRÉDITO';
  }
  
  return 'CONTADO';
};

export const obtenerNombreTipoCredito = (id) => {
  if (!id) return '';
  const idStr = id.toString().toUpperCase();
  if (idStr === 'CAS-001' || idStr.includes('CACHEA')) return 'CACHEAS';
  if (idStr === 'EMPRESA' || idStr === 'CORP' || idStr.includes('EMP')) return 'EMPRESA';
  return idStr;
};

export const getBadgeEstadoVenta = (estado) => {
  const estadoMap = {
    'Pagada': { texto: 'PAGADA', clase: 'estado-pagada' },
    'Completada': { texto: 'PAGADA', clase: 'estado-pagada' },
    'Pendiente': { texto: 'PENDIENTE', clase: 'estado-pendiente' },
    'Pendiente Aprobación': { texto: 'PENDIENTE', clase: 'estado-pendiente' },
    'Cancelada': { texto: 'CANCELADA', clase: 'estado-cancelada' },
    'Anulada': { texto: 'CANCELADA', clase: 'estado-cancelada' },
    'Aprobado': { texto: 'APROBADO', clase: 'estado-aprobado' },
    'Rechazado': { texto: 'RECHAZADO', clase: 'estado-rechazado' }
  };
  
  return estadoMap[estado] || { texto: estado || 'DESCONOCIDO', clase: 'estado-desconocido' };
};

export const getBadgeTipoVenta = (tipo, idTipoCredito = null) => {
  const tipoTexto = formatearTipoVenta(tipo, idTipoCredito);
  if (tipoTexto.includes('CRÉDITO')) {
    return { texto: tipoTexto, clase: 'tipo-credito' };
  }
  return { texto: 'CONTADO', clase: 'tipo-contado' };
};

// ==================== CÁLCULOS ====================

export const calcularEstadisticasVentas = (ventas) => {
  const ventasNorm = normalizarVentas(ventas);
  const total = ventasNorm.length;
  
  const pagadas = ventasNorm.filter(v => 
    v.estado?.toLowerCase() === 'pagada' || 
    v.estado === 'Completada'
  ).length;
  
  const pendientes = ventasNorm.filter(v => 
    v.estado?.toLowerCase() === 'pendiente' || 
    v.estado === 'Pendiente Aprobación'
  ).length;
  
  const canceladas = ventasNorm.filter(v => 
    v.estado?.toLowerCase() === 'cancelada' || 
    v.estado === 'Anulada'
  ).length;
  
  const ventasContado = ventasNorm.filter(v => 
    formatearTipoVenta(v.tipoVenta) === 'CONTADO'
  ).length;
  
  const ventasCredito = ventasNorm.filter(v => 
    formatearTipoVenta(v.tipoVenta) === 'CRÉDITO'
  ).length;
  
  const totalFacturado = ventasNorm.reduce((sum, v) => sum + (Number(v.totalBs) || 0), 0);
  const totalUSD = ventasNorm.reduce((sum, v) => sum + (Number(v.totalUSD) || 0), 0);
  const totalPagado = ventasNorm.filter(v => v.estado === 'Completada' || v.estado === 'Pagada')
    .reduce((sum, v) => sum + (Number(v.totalBs) || 0), 0);
  
  let totalItems = 0;
  ventasNorm.forEach(venta => {
    if (venta.detalles && Array.isArray(venta.detalles)) {
      venta.detalles.forEach(detalle => {
        totalItems += Number(detalle.cantidad) || 0;
      });
    }
  });
  
  const promedioVenta = pagadas > 0 ? totalPagado / pagadas : 0;
  const promedioItemsPorVenta = total > 0 ? totalItems / total : 0;
  
  const fechas = ventasNorm
    .map(v => v.fechaVenta)
    .filter(f => f)
    .map(f => new Date(f));
  
  const fechaMasAntigua = fechas.length > 0 
    ? new Date(Math.min(...fechas.map(f => f.getTime())))
    : null;
    
  const fechaMasReciente = fechas.length > 0 
    ? new Date(Math.max(...fechas.map(f => f.getTime())))
    : null;
  
  return {
    total,
    pagadas,
    pendientes,
    canceladas,
    porcentajePagadas: total > 0 ? Math.round((pagadas / total) * 100) : 0,
    porcentajePendientes: total > 0 ? Math.round((pendientes / total) * 100) : 0,
    porcentajeCanceladas: total > 0 ? Math.round((canceladas / total) * 100) : 0,
    ventasContado,
    ventasCredito,
    porcentajeContado: total > 0 ? Math.round((ventasContado / total) * 100) : 0,
    porcentajeCredito: total > 0 ? Math.round((ventasCredito / total) * 100) : 0,
    totalFacturado,
    totalUSD,
    totalPagado,
    totalItems,
    promedioItemsPorVenta,
    promedioVenta,
    fechaMasAntigua: fechaMasAntigua ? formatearFechaCorta(fechaMasAntigua.toISOString()) : 'N/A',
    fechaMasReciente: fechaMasReciente ? formatearFechaCorta(fechaMasReciente.toISOString()) : 'N/A',
    totalCobradoUSD: ventasNorm.reduce((sum, v) => sum + (Number(v.montoCobradoParaCaja) || 0), 0)
  };
};

export const filtrarVentasParaReporte = (ventas, filtro, estadoAdicional = null) => {
  let ventasFiltradas = [...ventas];
  
  switch (filtro) {
    case 'todos':
      break;
    case 'pagadas':
      ventasFiltradas = ventasFiltradas.filter(v => 
        v.estado?.toLowerCase() === 'pagada' || v.estado === 'Completada'
      );
      break;
    case 'pendientes':
      ventasFiltradas = ventasFiltradas.filter(v => 
        v.estado?.toLowerCase() === 'pendiente' || v.estado === 'Pendiente Aprobación'
      );
      break;
    case 'canceladas':
      ventasFiltradas = ventasFiltradas.filter(v => 
        v.estado?.toLowerCase() === 'cancelada' || v.estado === 'Anulada'
      );
      break;
    case 'contado':
      ventasFiltradas = ventasFiltradas.filter(v => 
        formatearTipoVenta(v.tipoVenta) === 'CONTADO'
      );
      break;
    case 'credito':
      ventasFiltradas = ventasFiltradas.filter(v => 
        formatearTipoVenta(v.tipoVenta) === 'CRÉDITO'
      );
      break;
    default:
      if (!isNaN(filtro) && filtro > 0) {
        ventasFiltradas = ventasFiltradas.filter(v => v.idCliente === filtro);
      }
      else if (!isNaN(filtro) && filtro < 0) {
        const vendedorId = Math.abs(filtro);
        ventasFiltradas = ventasFiltradas.filter(v => v.idVendedor === vendedorId);
      }
      break;
  }
  
  if (estadoAdicional && estadoAdicional !== 'all') {
    ventasFiltradas = ventasFiltradas.filter(v => v.estado?.toLowerCase() === estadoAdicional.toLowerCase());
  }
  
  return ventasFiltradas;
};

// ==================== GENERAR HTML ====================

export const generarHTMLReporteVentas = (ventas, titulo = null, filtro = 'todos', mostrarDetalles = true) => {
  const empresa = getEmpresa();   // 👈 NUEVO: datos dinámicos del tenant
  const ventasNormalizadas = normalizarVentas(ventas);
  const fechaActual = new Date();
  const estadisticas = calcularEstadisticasVentas(ventasNormalizadas);
  
  let tituloReal = titulo;
  if (!tituloReal) {
    const titulosPorFiltro = {
      'todos': 'REPORTE GENERAL DE VENTAS',
      'pagadas': 'VENTAS PAGADAS',
      'pendientes': 'VENTAS PENDIENTES',
      'canceladas': 'VENTAS CANCELADAS',
      'contado': 'VENTAS DE CONTADO',
      'credito': 'VENTAS A CRÉDITO'
    };
    tituloReal = titulosPorFiltro[filtro] || 'REPORTE DE VENTAS';
  }

  return `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${tituloReal} - ${empresa.nombre}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: 'Arial', 'Helvetica', sans-serif;
          background: white;
          padding: 20px;
        }
        .reporte { max-width: 1400px; margin: 0 auto; }
        
        .header {
          padding: 15px 0;
          border-bottom: 2px solid #333;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .logo-area { display: flex; align-items: center; gap: 15px; }
        .logo-img { height: 50px; width: auto; object-fit: contain; }
        .empresa-nombre { font-size: 18px; font-weight: bold; color: #333; }
        .empresa-slogan { font-size: 11px; color: #666; margin-top: 3px; }
        .rif-info { text-align: right; font-size: 11px; line-height: 1.4; color: #555; }
        
        .titulo { padding: 10px 0; }
        .titulo h1 { font-size: 20px; font-weight: bold; color: #1a4d8c; }
        .fecha { font-size: 11px; color: #666; margin-top: 5px; }
        
        .stats-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 12px;
          margin: 20px 0;
        }
        .stat-card {
          background: #f8f9fa;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 12px;
          text-align: center;
        }
        .stat-label {
          font-size: 11px;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 5px;
        }
        .stat-value {
          font-size: 22px;
          font-weight: bold;
          color: #1a4d8c;
        }
        .stat-value-small {
          font-size: 16px;
        }
        
        .tabla-container { 
          overflow-x: auto; 
          margin-top: 20px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
          border: 1px solid #ddd;
        }
        th {
          background: #f5f5f5;
          padding: 10px 8px;
          text-align: left;
          font-weight: bold;
          border-bottom: 2px solid #ddd;
          color: #333;
        }
        td {
          padding: 8px;
          border-bottom: 1px solid #eee;
          vertical-align: top;
        }
        tr:hover td { background: #fafafa; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        
        .tipo-contado {
          display: inline-block;
          padding: 3px 8px;
          background: #e8f5e9;
          color: #2e7d32;
          border-radius: 4px;
          font-size: 10px;
          font-weight: bold;
        }
        .tipo-credito {
          display: inline-block;
          padding: 3px 8px;
          background: #fff3e0;
          color: #ed6c02;
          border-radius: 4px;
          font-size: 10px;
          font-weight: bold;
        }
        
        .footer {
          margin-top: 30px;
          padding-top: 15px;
          border-top: 1px solid #ddd;
          font-size: 10px;
          color: #666;
          text-align: center;
        }
        
        .controles {
          text-align: center;
          margin-top: 20px;
          padding: 15px;
          background: #f5f5f5;
          border-radius: 8px;
        }
        button {
          padding: 8px 20px;
          font-size: 12px;
          margin: 0 5px;
          cursor: pointer;
          border: 1px solid #ccc;
          background: white;
          border-radius: 5px;
          transition: all 0.2s;
        }
        button:hover { background: #e0e0e0; }
        
        @media print {
          body { padding: 0; }
          .controles { display: none; }
          .stat-card { break-inside: avoid; }
          @page { margin: 1.5cm; }
        }
      </style>
    </head>
    <body>
      <div class="reporte">
        <div class="header">
          <div class="logo-area">
            ${getLogoBase64() ? `<img src="data:image/jpeg;base64,${getLogoBase64()}" class="logo-img" onerror="this.style.display='none'">` : ''}
            <div>
              <div class="empresa-nombre">${empresa.nombre}</div>
              <div class="empresa-slogan">${empresa.slogan}</div>
            </div>
          </div>
          <div class="rif-info">
            RIF: ${empresa.rif}<br>
            ${empresa.direccion || ''}<br>
            ${empresa.ciudad}
          </div>
        </div>
        
        <div class="titulo">
          <h1>${tituloReal}</h1>
          <div class="fecha">Generado: ${formatearFechaReporte(fechaActual.toISOString())}</div>
        </div>
        
        <div class="tabla-container">
          ${ventasNormalizadas.length > 0 ? `
            <table>
            <thead>
              <tr>
                <th>Factura</th>
                <th>Fecha</th>
                <th class="text-center">Tipo Venta</th>
                <th class="text-right">Total USD</th>
                <th class="text-right">Total Bs</th>
                <th>Método Pago</th>
                <th>Cliente</th>
                <th>Vendedor</th>
              </tr>
            </thead>
            <tbody>
              ${ventasNormalizadas.map(venta => {
                const numeroFactura = venta.numeroFactura || '—';
                const fechaVenta = venta.fechaVenta;
                const tipoVenta = venta.tipoVenta;
                const idTipoCredito = venta.idTipoCredito;
                const totalUSD = venta.totalUSD || 0;
                const totalBs = venta.totalBs || 0;
                const nombreCliente = venta.nombreCliente || 'Consumidor Final';
                const nombreVendedor = venta.nombreVendedor || '—';
                const metodoPago = venta.metodoPago || '—';
                const badgeTipo = getBadgeTipoVenta(tipoVenta, idTipoCredito);
                const montoAbonado = venta.montoPagado || 0;
                
                let indicadorCaja = '';
                if (venta.esCredito) {
                  if (montoAbonado > 0) {
                    indicadorCaja = `<br><span style="color:green; font-size:9px; font-weight:bold;">Abonó: $${Number(montoAbonado).toFixed(2)}</span>`;
                  } else {
                    indicadorCaja = `<br><span style="color:#999; font-size:9px; font-weight:bold;">Sin abono</span>`;
                  }
                }
                
                return `
                  <tr${venta.esCredito && montoAbonado <= 0 ? ' style="opacity: 0.7;"' : ''}>
                    <td><strong>${numeroFactura}</strong></td>
                    <td>${formatearFechaCorta(fechaVenta)}</td>
                    <td class="text-center"><span class="${badgeTipo.clase}">${badgeTipo.texto}</span></td>
                    <td class="text-right">
                      <strong>$ ${Number(totalUSD).toFixed(2)}</strong>
                      ${indicadorCaja}
                    </td>
                    <td class="text-right"><strong>${formatearMonedaBs(totalBs)}</strong></td>
                    <td>${metodoPago}</td>
                    <td>${nombreCliente}</td>
                    <td>${nombreVendedor}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
            <tfoot style="background: #f5f5f5; font-weight: bold; border-top: 2px solid #333;">
              <tr style="height: 20px;">
                <td colspan="3" class="text-right" style="padding: 4px 8px; white-space: nowrap;">TOTAL FACTURADO:</td>
                <td class="text-right" style="padding: 4px 8px; white-space: nowrap;">$${estadisticas.totalUSD.toFixed(2)}</td>
                <td class="text-right" style="padding: 4px 8px; white-space: nowrap;">${formatearMonedaBs(estadisticas.totalFacturado)}</td>
                <td colspan="3"></td>
              </tr>
              <tr style="background: #e8f5e9; color: #2e7d32; height: 25px;">
                <td colspan="3" class="text-right" style="padding: 4px 8px; white-space: nowrap; font-weight: 900;">TOTAL REAL COBRADO (CAJA):</td>
                <td class="text-right" style="padding: 4px 8px; white-space: nowrap; font-size: 13px; font-weight: 900;">$${estadisticas.totalCobradoUSD.toFixed(2)}</td>
                <td class="text-right" style="padding: 4px 8px; white-space: nowrap; font-size: 13px; font-weight: 900;">${formatearMonedaBs(estadisticas.totalCobradoUSD * (estadisticas.totalFacturado / estadisticas.totalUSD || 1))}</td>
                <td colspan="3" style="font-style: italic; font-size: 8px; padding: 4px 8px; white-space: nowrap;">* Dinero real percibido</td>
              </tr>
            </tfoot>
            </table>
          ` : '<div style="padding: 40px; text-align: center; border: 1px solid #ddd; border-radius: 8px;">No hay ventas para mostrar</div>'}
        </div>
        
        <div class="footer">
          ${empresa.nombre} - Sistema de Gestión de Ventas
        </div>
      </div>
      
      <div class="controles">
        <button onclick="window.print()">🖨️ Imprimir Reporte</button>
        <button onclick="window.close()">✖️ Cerrar</button>
      </div>
    </body>
    </html>
  `;
};

export const generarHTMLReporteVentasSimplificado = (ventas, titulo = null) => {
  const empresa = getEmpresa();   // 👈 NUEVO: datos dinámicos del tenant
  const ventasNormalizadas = normalizarVentas(ventas);
  const fechaActual = new Date();
  const tituloReal = titulo || 'LISTADO DE VENTAS';
  
  const totalGeneralUSD = ventasNormalizadas.reduce((sum, v) => sum + (Number(v.totalUSD) || 0), 0);
  const totalGeneralBs = ventasNormalizadas.reduce((sum, v) => sum + (Number(v.totalBs) || 0), 0);
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${tituloReal} - ${empresa.nombre}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;}
        body{font-family:Arial;background:white;padding:20px;}
        .header{display:flex;align-items:center;gap:15px;margin-bottom:20px;padding:15px;background:#f8f8f8;border-radius:8px;}
        .logo-img{height:45px;width:auto;object-fit:contain;}
        .header-text h1{font-size:16px;color:#333;}
        .header-text p{font-size:11px;color:#666;}
        .titulo{font-size:18px;font-weight:bold;border-bottom:2px solid #333;margin-bottom:15px;padding-bottom:8px;color:#1a4d8c;}
        .fecha{font-size:11px;color:#666;margin-bottom:20px;}
        table{width:100%;border-collapse:collapse;font-size:11px;border:1px solid #ddd;}
        th{background:#f5f5f5;padding:10px 8px;text-align:left;font-weight:bold;border-bottom:2px solid #ddd;}
        td{padding:8px;border-bottom:1px solid #eee;}
        .text-right{text-align:right;}
        .text-center{text-align:center;}
        .footer{margin-top:25px;padding-top:15px;border-top:1px solid #ddd;font-size:10px;color:#666;text-align:center;}
        .controles{text-align:center;margin-top:20px;padding:15px;}
        button{padding:8px 20px;font-size:12px;margin:0 5px;cursor:pointer;border:1px solid #ccc;background:white;border-radius:5px;}
        button:hover{background:#f0f0f0;}
        @media print{.controles{display:none;}}
      </style>
    </head>
    <body>
      <div class="header">
        ${getLogoBase64() ? `<img src="data:image/jpeg;base64,${getLogoBase64()}" class="logo-img" onerror="this.style.display='none'">` : ''}
        <div class="header-text">
          <h1>${empresa.nombre}</h1>
          <p>${empresa.slogan}</p>
        </div>
      </div>
      <div class="titulo">${tituloReal}</div>
      <div class="fecha">Generado: ${formatearFechaReporte(fechaActual.toISOString())}</div>
      <table>
        <thead>
          <tr>
            <th>Factura</th>
            <th>Fecha</th>
            <th class="text-center">Tipo Venta</th>
            <th class="text-right">Total USD</th>
            <th class="text-right">Total Bs</th>
            <th>Método Pago</th>
            <th>Cliente</th>
            <th>Vendedor</th>
          </tr>
        </thead>
        <tbody>
          ${ventasNormalizadas.map(v => {
            const numeroFactura = v.numeroFactura || '—';
            const fechaVenta = v.fechaVenta;
            const totalUSD = v.totalUSD || 0;
            const totalBs = v.totalBs || 0;
            const nombreCliente = v.nombreCliente || 'Consumidor Final';
            const nombreVendedor = v.nombreVendedor || '—';
            const metodoPago = v.metodoPago || '—';
            const idTipoCredito = v.idTipoCredito || null;
            return `
              <tr>
                <td><strong>${numeroFactura}</strong></td>
                <td>${formatearFechaCorta(fechaVenta)}</td>
                <td class="text-center">${formatearTipoVenta(v.tipoVenta, idTipoCredito)}</td>
                <td class="text-right">$ ${Number(totalUSD).toFixed(2)}</td>
                <td class="text-right">${formatearMonedaBs(totalBs)}</td>
                <td>${metodoPago}</td>
                <td>${nombreCliente}</td>
                <td>${nombreVendedor}</td>
              </tr>
            `;
          }).join('')}
        </tbody>
        <tfoot>
          <tr style="background: #f5f5f5; font-weight: bold;">
            <td colspan="3" class="text-right">TOTALES:</td>
            <td class="text-right">$ ${totalGeneralUSD.toFixed(2)}</td>
            <td class="text-right">${formatearMonedaBs(totalGeneralBs)}</td>
            <td colspan="3"></td>
          </tr>
        </tfoot>
      </table>
      <div class="footer">
        ${empresa.nombre} - Sistema de Gestión de Ventas
      </div>
      <div class="controles">
        <button onclick="window.print()">🖨️ Imprimir</button>
        <button onclick="window.close()">✖️ Cerrar</button>
      </div>
    </body>
    </html>
  `;
};

// ==================== IMPRIMIR ====================

export const imprimirReporteVentas = async (ventas, titulo = null, filtro = 'todos', mostrarDetalles = true, subtitulo = '', incluirEstadisticas = true) => {
  if (!ventas || ventas.length === 0) {
    alert('No hay ventas para mostrar');
    return false;
  }
  
  try {
    // Resetear logo para que se recargue el del tenant actual
    LOGO_BASE64 = '';
    await cargarLogo();

    const htmlReporte = generarHTMLReporteVentas(ventas, titulo, filtro, mostrarDetalles);
    
    const ventana = window.open('', '_blank', 'width=1200,height=700,menubar=yes,toolbar=yes,location=yes,status=yes,scrollbars=yes');
    
    if (!ventana) {
      alert('Por favor, permite ventanas emergentes para generar el reporte');
      return false;
    }
    
    ventana.document.write(htmlReporte);
    ventana.document.close();
    ventana.focus();
    
    return true;
  } catch (error) {
    console.error('Error generando reporte:', error);
    alert('Error al generar el reporte: ' + error.message);
    return false;
  }
};

export const imprimirReporteVentasSimplificado = async (ventas, titulo = null) => {
  if (!ventas || ventas.length === 0) {
    alert('No hay ventas para mostrar');
    return false;
  }
  
  try {
    LOGO_BASE64 = '';
    await cargarLogo();

    const htmlReporte = generarHTMLReporteVentasSimplificado(ventas, titulo);
    const ventana = window.open('', '_blank', 'width=1200,height=700,menubar=yes,toolbar=yes,location=yes,status=yes');
    
    if (!ventana) {
      alert('Por favor, permite ventanas emergentes para generar el reporte');
      return false;
    }
    
    ventana.document.write(htmlReporte);
    ventana.document.close();
    ventana.focus();
    return true;
  } catch (error) {
    console.error('Error generando reporte simplificado:', error);
    alert('Error al generar el reporte');
    return false;
  }
};

// ==================== EXCEL ====================

export const exportarVentasExcel = (ventas, titulo = 'ventas', filtro = 'todos') => {
  try {
    const ventasNormalizadas = normalizarVentas(ventas);
    
    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();
    
    const data = ventasNormalizadas.map(venta => ({
      'N° Factura': venta.numeroFactura || '',
      'Fecha': formatearFechaCorta(venta.fechaVenta),
      'Tipo Venta': formatearTipoVenta(venta.tipoVenta),
      'Total USD': Number(venta.totalUSD || 0),
      'Total Bs': Number(venta.totalBs || 0),
      'Cliente': venta.nombreCliente || '',
      'Vendedor': venta.nombreVendedor || ''
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [
      { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 15 }, { wch: 25 }, { wch: 20 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Ventas');
    
    const fechaStr = new Date().toISOString().split('T')[0];
    const nombreArchivo = `ventas_${titulo}_${fechaStr}.xlsx`;
    XLSX.writeFile(wb, nombreArchivo);
    
    return { success: true, filename: nombreArchivo, count: ventas.length };
  } catch (error) {
    console.error('Error exportando a Excel:', error);
    return { success: false, error: error.message };
  }
};

// Exportar todo
export default {
  JEAN_INFORMATICO_CONFIG,
  cargarLogo,
  getLogoBase64,
  formatearFechaReporte,
  formatearFechaCorta,
  formatearNumero,
  formatearMonedaUSD,
  formatearMonedaBs,
  convertirEstadoTexto,
  formatearTipoVenta,
  getBadgeEstadoVenta,
  getBadgeTipoVenta,
  calcularEstadisticasVentas,
  filtrarVentasParaReporte,
  normalizarVenta,
  normalizarVentas,
  generarHTMLReporteVentas,
  generarHTMLReporteVentasSimplificado,
  imprimirReporteVentas,
  imprimirReporteVentasSimplificado,
  exportarVentasExcel
};